import { computed, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { isBoolean, isEmpty, isNil } from 'lodash';

import { YardTaskCheckLocationOutcome, YardTaskInventoryGroupType } from '@nova/core';
import yardViewService from '@satellite/services/yard-view-service';
import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from '@satellite/composables/queryKeys';
import { useEventHub } from '@satellite/composables/useEventHub';
import { useYardInventoryCounts } from '@satellite/composables/useYardInventoryCounts';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe, Nullable } from '@nova/core';

export const INVENTORY_BUCKET_MISSING = '__missing__';
export const INVENTORY_BUCKET_WAITING = '__waiting__';

export type InventoryBucketKind = 'missing' | 'waiting' | 'area';

/** Lean asset projection — exactly the fields `YardAssetPresenterV2` needs to seed code + chip. */
export interface InventoryRowAsset {
  id: string;
  code: string;
  type: Nullable<string>;
  state: Nullable<string>;
}

/** Lean spot/area projections — rendered as plain location text in the row (no nested presenter). */
export interface InventoryRowSpot {
  id: string;
  code: string;
}

export interface InventoryRowArea {
  id: string;
  name: string;
}

/**
 * Row VM consumed by `YardInventoryAssetRow`, projected straight from the BFF response. Honest
 * lean shapes (no `as unknown as`): `assetContainer` seeds `YardAssetPresenterV2` as authoritative
 * initial data; `spot`/`area` are rendered as location text.
 */
export interface InventoryAssetRow {
  assetContainer: InventoryRowAsset;
  spot: Maybe<InventoryRowSpot>;
  area: Maybe<InventoryRowArea>;
  bucketKey: string;
  bucketKind: InventoryBucketKind;
  outcome: Maybe<YardTaskCheckLocationOutcome>;
}

export interface InventoryBucket {
  key: string;
  kind: InventoryBucketKind;
  /** Human label; for `area` buckets this is the area name (falls back to id for unnamed areas). */
  name: string;
  areaId: Maybe<string>;
  rows: InventoryAssetRow[];
  totalCount: number;
  inScope?: boolean;
}

interface InventoryCounts {
  checked: number;
  missing: number;
  pending: number;
  total: number;
}

/** Shape of `GET /yard/view/inventory-check` (OD-3625). Kept local — these are neutron BFF DTOs. */
interface InventoryCheckLocation {
  spotId: string;
  spotCode: string;
  spotAreaId: string;
  spotAreaName: string;
}

interface InventoryCheckContainer {
  id: string;
  code: string;
  type: Nullable<string>;
  state: Nullable<string>;
  location: Nullable<InventoryCheckLocation>;
  outcome: Nullable<YardTaskCheckLocationOutcome>;
}

interface InventoryCheckGroup {
  type: YardTaskInventoryGroupType;
  spotAreaId: Nullable<string>;
  spotAreaName: Nullable<string>;
  count: number;
  containers: InventoryCheckContainer[];
}

interface InventoryCheckResponse {
  summary: InventoryCounts;
  groups: InventoryCheckGroup[];
}

export interface UseYardInventoryOptions {
  /** Task whose inventory scope is resolved (required input; the BFF query keys off this). */
  taskId: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;
  /** Warehouse scope, used only to guard real-time EventHub invalidations. */
  warehouseId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;
  /** Bypass viewport gating when the consumer has no v-intersect host (title chip, completion gate). */
  headless?: boolean;
  /** Standard presenter mode. Collection presenters default to `live` per repo conventions. */
  mode?: 'live';
  /** TanStack staleTime; defaults to `Infinity` per repo conventions. */
  staleTime?: number;
}

const EMPTY_SUMMARY: InventoryCounts = { total: 0, checked: 0, missing: 0, pending: 0 };
const EMPTY_RESPONSE: InventoryCheckResponse = { summary: EMPTY_SUMMARY, groups: [] };

/** Resolves a bucket's display name; missing/waiting are i18n keys, areas keep their server name. */
type BucketLabel = (kind: InventoryBucketKind, areaName: string) => string;

/** Map a BFF group to its bucket identity (key/kind/areaId + raw area name for `area` groups). */
function bucketIdentity(group: InventoryCheckGroup): {
  key: string;
  kind: InventoryBucketKind;
  areaId: Maybe<string>;
  areaName: string;
} {
  if (group.type === YardTaskInventoryGroupType.Missing) {
    return { key: INVENTORY_BUCKET_MISSING, kind: 'missing', areaId: null, areaName: '' };
  }
  if (group.type === YardTaskInventoryGroupType.Unassigned) {
    return { key: INVENTORY_BUCKET_WAITING, kind: 'waiting', areaId: null, areaName: '' };
  }
  const areaId = group.spotAreaId ?? null;
  return {
    // A spotArea group always carries a spotAreaId; the fallback only guards a degenerate payload
    // and must stay distinct from the waiting bucket key to avoid colliding `:key`s in the list.
    key: areaId ?? `__area__${group.spotAreaName ?? 'unknown'}`,
    kind: 'area',
    areaId,
    areaName: group.spotAreaName ?? areaId ?? ''
  };
}

/** Project a BFF container into the row VM consumed by `YardInventoryAssetRow`. */
function projectRow(
  container: InventoryCheckContainer,
  identity: ReturnType<typeof bucketIdentity>
): InventoryAssetRow {
  const location = container.location;
  const hasLocation = !isNil(location);
  return {
    assetContainer: {
      id: container.id,
      code: container.code,
      type: container.type,
      state: container.state
    },
    spot: hasLocation ? { id: location.spotId, code: location.spotCode } : null,
    area: hasLocation ? { id: location.spotAreaId, name: location.spotAreaName } : null,
    bucketKey: identity.key,
    bucketKind: identity.kind,
    outcome: container.outcome ?? null
  };
}

/** Map the BFF response (already deduplicated + ordered server-side) into bucket VMs. */
function toBuckets(response: InventoryCheckResponse, bucketLabel: BucketLabel): InventoryBucket[] {
  return response.groups.map(group => {
    const identity = bucketIdentity(group);
    const containers = Array.isArray(group.containers) ? group.containers : [];
    return {
      key: identity.key,
      kind: identity.kind,
      areaId: identity.areaId,
      name: bucketLabel(identity.kind, identity.areaName),
      rows: containers.map(container => projectRow(container, identity)),
      totalCount: containers.length
    };
  });
}

function normalizeResponse(raw: unknown): InventoryCheckResponse {
  const response = raw as Partial<InventoryCheckResponse> | null | undefined;
  const summary = response?.summary;
  const groups = response?.groups;
  return {
    summary: {
      total: summary?.total ?? 0,
      checked: summary?.checked ?? 0,
      missing: summary?.missing ?? 0,
      pending: summary?.pending ?? 0
    },
    groups: Array.isArray(groups) ? groups : []
  };
}

function tallyCounts(rows: InventoryAssetRow[]): InventoryCounts {
  let checked = 0;
  let missing = 0;
  let pending = 0;
  for (const row of rows) {
    if (row.outcome === YardTaskCheckLocationOutcome.CONFIRMED) {
      checked += 1;
    } else if (row.outcome === YardTaskCheckLocationOutcome.MISSING) {
      missing += 1;
    } else {
      pending += 1;
    }
  }
  return { checked, missing, pending, total: rows.length };
}

export function useYardInventory(options: UseYardInventoryOptions) {
  const { taskId, warehouseId, headless = false, mode = 'live', staleTime = Infinity } = options;
  const queryClient = useQueryClient();
  const eventHub = useEventHub();
  const { t } = useI18n();

  // Missing/Waiting bucket names go through i18n; area buckets keep their server-provided name.
  const bucketLabel: BucketLabel = (kind, areaName) => {
    if (kind === 'missing') {
      return t('yardInventory.sections.missing');
    }
    if (kind === 'waiting') {
      return t('yardInventory.sections.waitingAssignment');
    }
    return areaName;
  };

  const effectiveTaskId = computed<Maybe<string>>(() => unref(taskId) || null);
  const effectiveWarehouseId = computed<Maybe<string>>(() => unref(warehouseId) || null);

  const warehouseScope = useYardInventoryCounts({
    warehouseId: effectiveWarehouseId,
    headless: true
  });

  const yardInventoryQueryKey = computed(() =>
    effectiveTaskId.value
      ? (queryKeys.yardInventoryCheck(effectiveTaskId.value) as readonly unknown[])
      : ([] as readonly unknown[])
  );

  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const invalidate = (): void => {
    if (isEmpty(yardInventoryQueryKey.value)) {
      return;
    }
    void queryClient.invalidateQueries(
      { queryKey: yardInventoryQueryKey.value },
      DEFAULT_INVALIDATE_OPTIONS
    );
  };

  const onIntersect = (entry?: { isIntersecting?: boolean } | boolean): void => {
    if (headless) {
      return;
    }
    const isIntersecting = isBoolean(entry) ? entry : (entry?.isIntersecting ?? true);
    isInViewport.value = isIntersecting;
    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  async function fetchInventoryCheck(
    signal: AbortSignal | undefined,
    params?: { types: YardTaskInventoryGroupType[]; spotAreaIds: string[] }
  ): Promise<InventoryCheckResponse> {
    const tid = effectiveTaskId.value;
    if (isNil(tid)) {
      return EMPTY_RESPONSE;
    }
    const response = await yardViewService.getInventoryCheck(tid, {
      requestConfig: { signal },
      enableCache: false,
      // `selectionParams` only ever emits Missing/Unassigned here; the JS service types `types` as
      // the `'missing' | 'unassigned'` string CSV, so narrow the string-enum members to match.
      types: params?.types as Array<'missing' | 'unassigned'> | undefined,
      spotAreaIds: params?.spotAreaIds
    });
    return normalizeResponse(response);
  }

  const search = ref<string>('');
  const filterSelection = ref<Set<string>>(new Set<string>());
  const filterInitialized = ref<boolean>(false);
  const previousBucketKeys = ref<Set<string>>(new Set<string>());

  /**
   * Catalog query — the task's full resolved scope (no filter params). Drives the filter dialog's
   * bucket list and the scope-wide counters, independent of the current selection.
   */
  const catalogQuery = useQuery<InventoryCheckResponse>({
    queryKey: yardInventoryQueryKey,
    enabled: computed(() => !isNil(effectiveTaskId.value) && isInViewport.value),
    staleTime,
    refetchOnWindowFocus: false,
    // Keep the previous scope/counts while refetching or re-keying so the counters never
    // flash to zero (e.g. on task completion) — OD-3837 A4.
    placeholderData: previous => previous,
    queryFn: ({ signal }) => fetchInventoryCheck(signal)
  });

  const catalogResponse = computed<InventoryCheckResponse>(
    () => catalogQuery.data.value ?? EMPTY_RESPONSE
  );

  /** The task's resolved scope (its stored groups). Drives the default selection + scope counters. */
  const allBuckets = computed<InventoryBucket[]>(() =>
    toBuckets(catalogResponse.value, bucketLabel)
  );

  /**
   * Filter options: every active area in the warehouse plus the missing/waiting buckets, regardless
   * of the task's stored groups. The selector lists all of these; the task's groups are pre-checked.
   */
  const filterBuckets = computed<InventoryBucket[]>(() => {
    const scopedKeys = new Set(allBuckets.value.map(bucket => bucket.key));
    const buckets: InventoryBucket[] = [
      {
        key: INVENTORY_BUCKET_MISSING,
        kind: 'missing',
        areaId: null,
        name: bucketLabel('missing', ''),
        rows: [],
        totalCount: warehouseScope.missing.value,
        inScope: scopedKeys.has(INVENTORY_BUCKET_MISSING)
      },
      {
        key: INVENTORY_BUCKET_WAITING,
        kind: 'waiting',
        areaId: null,
        name: bucketLabel('waiting', ''),
        rows: [],
        totalCount: warehouseScope.waiting.value,
        inScope: scopedKeys.has(INVENTORY_BUCKET_WAITING)
      }
    ];
    for (const area of warehouseScope.areas.value) {
      buckets.push({
        key: area.spotAreaId,
        kind: 'area',
        areaId: area.spotAreaId,
        name: bucketLabel('area', area.spotAreaName),
        rows: [],
        totalCount: area.count,
        inScope: scopedKeys.has(area.spotAreaId)
      });
    }
    return buckets;
  });

  /**
   * The warehouse-counts query has settled at least once. The option set always carries the
   * injected missing/waiting buckets, so its size can't tell us whether the area buckets have
   * arrived — gate the initial seed on this instead, or a slow counts response would drop every
   * stored area bucket and `previousBucketKeys` would block them from re-joining later.
   */
  const warehouseScopeReady = computed(() => warehouseScope.query.isFetched.value);

  /**
   * On first catalog load, seed the selection to all buckets. Afterwards only reconcile for bucket
   * churn (dropped buckets leave the selection, new buckets join); a deliberate "deselect all"
   * survives refetches because we compare against the previous BUCKET set, not the SELECTION.
   */
  watch(
    [filterBuckets, allBuckets, warehouseScopeReady],
    ([options, taskBuckets, scopeReady]) => {
      const optionKeys = new Set(options.map(bucket => bucket.key));
      const taskKeys = taskBuckets.map(bucket => bucket.key);
      if (!filterInitialized.value && scopeReady && taskKeys.length > 0) {
        filterSelection.value = new Set(taskKeys.filter(key => optionKeys.has(key)));
        previousBucketKeys.value = new Set(taskKeys);
        filterInitialized.value = true;
        return;
      }
      const merged = new Set<string>();
      for (const key of filterSelection.value) {
        if (optionKeys.has(key)) {
          merged.add(key);
        }
      }
      for (const key of taskKeys) {
        if (!previousBucketKeys.value.has(key) && optionKeys.has(key)) {
          merged.add(key);
        }
      }
      filterSelection.value = merged;
      previousBucketKeys.value = new Set(taskKeys);
    },
    { immediate: true, deep: false }
  );

  /** The current selection as explicit BFF filter params (types + spot areas), once initialized. */
  const selectionParams = computed<{
    types: YardTaskInventoryGroupType[];
    spotAreaIds: string[];
  } | null>(() => {
    if (!filterInitialized.value) {
      return null;
    }
    const selection = filterSelection.value;
    const types: YardTaskInventoryGroupType[] = [];
    if (selection.has(INVENTORY_BUCKET_MISSING)) {
      types.push(YardTaskInventoryGroupType.Missing);
    }
    if (selection.has(INVENTORY_BUCKET_WAITING)) {
      types.push(YardTaskInventoryGroupType.Unassigned);
    }
    const spotAreaIds = [...selection]
      .filter(key => key !== INVENTORY_BUCKET_MISSING && key !== INVENTORY_BUCKET_WAITING)
      .sort();
    return { types, spotAreaIds };
  });

  /** Nothing selected → show an empty list without hitting the BFF (empty params = full scope). */
  const selectionEmpty = computed(() => filterInitialized.value && isEmpty(filterSelection.value));

  const listingQueryKey = computed(() => {
    if (!effectiveTaskId.value) {
      return [] as readonly unknown[];
    }
    const base = queryKeys.yardInventoryCheck(effectiveTaskId.value);
    const params = selectionParams.value;
    return (params ? [...base, params] : base) as readonly unknown[];
  });

  /**
   * Listing query — the server-resolved selection (filter params). Drives the panel rows. When the
   * selection is "everything" its key collapses to the catalog key, so it shares that single fetch.
   */
  const listingQuery = useQuery<InventoryCheckResponse>({
    queryKey: listingQueryKey,
    enabled: computed(
      () => !isNil(effectiveTaskId.value) && isInViewport.value && !selectionEmpty.value
    ),
    staleTime,
    refetchOnWindowFocus: false,
    queryFn: ({ signal }) => fetchInventoryCheck(signal, selectionParams.value ?? undefined)
  });

  const listingResponse = computed<InventoryCheckResponse>(
    () => listingQuery.data.value ?? EMPTY_RESPONSE
  );

  const visibleBuckets = computed<InventoryBucket[]>(() => {
    if (selectionEmpty.value) {
      return [];
    }
    const term = search.value.trim().toLowerCase();
    const rowsByKey = new Map(
      toBuckets(listingResponse.value, bucketLabel).map(bucket => [bucket.key, bucket.rows])
    );
    return filterBuckets.value
      .filter(bucket => filterSelection.value.has(bucket.key))
      .map(option => {
        const rows = rowsByKey.get(option.key) ?? [];
        return {
          ...option,
          rows: isEmpty(term)
            ? rows
            : rows.filter(row => row.assetContainer.code?.toLowerCase().includes(term))
        };
      });
  });

  const visibleRows = computed<InventoryAssetRow[]>(() =>
    visibleBuckets.value.flatMap(bucket => bucket.rows)
  );

  const allRows = computed<InventoryAssetRow[]>(() =>
    allBuckets.value.flatMap(bucket => bucket.rows)
  );

  // Panel header counts reflect the filtered view; scope-wide counters (title) come from the
  // catalog summary, independent of the filter.
  const counts = computed<InventoryCounts>(() => tallyCounts(visibleRows.value));
  const warehouseCounts = computed<InventoryCounts>(() => catalogResponse.value.summary);

  // EventHub: scope-guarded handlers per canonical V2 presenter pattern. Each consumer
  // (panel/title/gate) registers its own; TanStack dedupes the resulting refetches by key.
  const isInScope = (event: { warehouseId?: Maybe<string> }): boolean => {
    if (!isEmpty(event?.warehouseId) && !isEmpty(effectiveWarehouseId.value)) {
      return event.warehouseId === effectiveWarehouseId.value;
    }
    return true;
  };

  const requestInvalidate = (event: { warehouseId?: Maybe<string> }): void => {
    if (!isInScope(event)) {
      return;
    }
    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }
    invalidate();
  };

  watch(effectiveTaskId, (next, prev) => {
    if (next !== prev) {
      // Switching tasks must not carry over the previous task's filter/search view.
      pendingInvalidation.value = false;
      search.value = '';
      filterSelection.value = new Set<string>();
      previousBucketKeys.value = new Set<string>();
      filterInitialized.value = false;
    }
  });

  const INVALIDATING_EVENTS = [
    'update-YardTask',
    'create-YardTaskCheckAssetContainer',
    'update-YardTaskCheckAssetContainer',
    'delete-YardTaskCheckAssetContainer',
    'create-AssetContainer',
    'update-AssetContainer',
    'delete-AssetContainer',
    'create-AssetContainerEvent',
    'update-AssetContainerEvent',
    'delete-AssetContainerEvent',
    'create-SpotAssignment',
    'update-SpotAssignment',
    'delete-SpotAssignment'
  ] as const;

  onMounted(() => {
    if (mode !== 'live') {
      return;
    }
    for (const eventName of INVALIDATING_EVENTS) {
      eventHub.on(eventName, requestInvalidate);
    }
  });

  onBeforeUnmount(() => {
    if (mode !== 'live') {
      return;
    }
    for (const eventName of INVALIDATING_EVENTS) {
      eventHub.off(eventName, requestInvalidate);
    }
  });

  const isLoading = computed(() => catalogQuery.isLoading.value || listingQuery.isLoading.value);
  const isFetching = computed(() => catalogQuery.isFetching.value || listingQuery.isFetching.value);
  const error = computed(() => listingQuery.error.value ?? catalogQuery.error.value);
  const refresh = async (): Promise<void> => {
    await Promise.all([catalogQuery.refetch(), listingQuery.refetch()]);
  };

  return {
    data: allRows,
    isLoading,
    isFetching,
    error,
    refresh,
    invalidate,
    onIntersect,
    query: listingQuery,

    // Inventory-specific projections kept alongside the standard contract.
    allBuckets,
    filterBuckets,
    visibleBuckets,
    counts,
    warehouseCounts,
    search,
    filterSelection
  };
}
